import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { cn, localDate, localDateToMinutes } from '@/platform/lib/utils';
import {
    Button,
    Container,
    FilterBarDropdown,
    FilterBarPlaceholder,
    FilterBarToggle,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Deferred, router, usePage } from '@inertiajs/react';
import dayjs from 'dayjs';

const periodOptions = [
    {
        id: 'current',
        name: dayjs().format('MMMM YYYY'),
    },
    {
        id: 'one',
        name: dayjs().subtract(1, 'months').format('MMMM YYYY'),
    },
    {
        id: 'two',
        name: dayjs().subtract(2, 'months').format('MMMM YYYY'),
    },
    {
        id: 'three',
        name: dayjs().subtract(3, 'months').format('MMMM YYYY'),
    },
    {
        id: 'four',
        name: dayjs().subtract(4, 'months').format('MMMM YYYY'),
    },
    {
        id: 'five',
        name: dayjs().subtract(5, 'months').format('MMMM YYYY'),
    },
];

const legend = [
    {
        className: 'bg-green-50 ring-green-300',
        description:
            'Het aantal nieuwe aanstellingen voor de periode is hetzelfde.',
    },
    {
        className: 'bg-yellow-50 ring-yellow-300',
        description:
            'Het aantal nieuwe aanstellingen voor de periode is hoger op het Platform.',
    },
    {
        className: 'bg-red-50 ring-red-300',
        description:
            'Het aantal nieuwe aanstellingen voor de periode is hoger op het Datahub.',
    },
];

const StatisticView = () => {
    const { statistics, last_updated_at, customers } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters(['statistics']);

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader
                    header='Datahub statistieken'
                    subheader={`Laatst gesynchroniseerd op: ${localDate(last_updated_at)}`}
                >
                    <Button
                        variant='primary'
                        onClick={() =>
                            router.post(
                                '/datahub/statistics',
                                {},
                                {
                                    preserveScroll: true,
                                    preserveState: true,
                                }
                            )
                        }
                    >
                        Synchroniseren
                    </Button>
                </SegmentHeader>

                <SegmentContent>
                    <SearchBarFilter only={['statistics']}>
                        <Deferred
                            data='customers'
                            fallback={<FilterBarPlaceholder />}
                        >
                            <FilterBarDropdown
                                clearable
                                label={
                                    customers?.find(
                                        c => c.id == filters?.customer_id
                                    )?.name || 'Alle klanten'
                                }
                                value={filters?.customer_id || ''}
                                options={customers || []}
                                onChange={value =>
                                    updateFilters('customer_id', value)
                                }
                            />
                        </Deferred>

                        <FilterBarDropdown
                            label={
                                periodOptions?.find(
                                    p => p.id == filters?.period
                                )?.name || 'Datahub > Platform'
                            }
                            clearable
                            options={periodOptions}
                            value={filters?.period || ''}
                            onChange={value => updateFilters('period', value)}
                        />
                        <FilterBarToggle
                            label='Datahub problemen met synchronisatie'
                            checked={!!filters?.datahub_sync_issues || false}
                            onChange={checked =>
                                updateFilters(
                                    'datahub_sync_issues',
                                    checked ? 1 : 0
                                )
                            }
                        />
                        <FilterBarToggle
                            label='Platform problemen met synchronisatie'
                            checked={!!filters?.platform_sync_issues || false}
                            onChange={checked =>
                                updateFilters(
                                    'platform_sync_issues',
                                    checked ? 1 : 0
                                )
                            }
                        />
                    </SearchBarFilter>

                    <div className='mb-4 flex justify-end gap-1.5'>
                        {legend.map(item => (
                            <Tooltip key={item.className}>
                                <TooltipTrigger>
                                    <span
                                        className={cn(
                                            'block size-5 rounded ring-1 ring-inset',
                                            item.className
                                        )}
                                    />
                                </TooltipTrigger>
                                <TooltipContent>
                                    {item.description}
                                </TooltipContent>
                            </Tooltip>
                        ))}
                    </div>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    rowSpan={2}
                                    sorted={isSorted('customer')}
                                    onClick={() =>
                                        updateFilters('sort', 'customer')
                                    }
                                >
                                    Klant
                                </TableHeaderCell>
                                <TableHeaderCell
                                    rowSpan={2}
                                    sorted={isSorted('entity')}
                                    onClick={() =>
                                        updateFilters('sort', 'entity')
                                    }
                                >
                                    Entiteit
                                </TableHeaderCell>
                                <TableHeaderCell
                                    rowSpan={2}
                                    sorted={isSorted('last_datahub_sync_at')}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'last_datahub_sync_at'
                                        )
                                    }
                                >
                                    Laatste sync Datahub
                                </TableHeaderCell>
                                <TableHeaderCell
                                    rowSpan={2}
                                    sorted={isSorted('last_platform_sync_at')}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'last_platform_sync_at'
                                        )
                                    }
                                >
                                    Laatste sync Platform
                                </TableHeaderCell>
                                <TableHeaderCell colSpan={2} textAlign='center'>
                                    {dayjs().format('MMMM YYYY')}
                                </TableHeaderCell>
                                <TableHeaderCell colSpan={2} textAlign='center'>
                                    {dayjs()
                                        .subtract(1, 'months')
                                        .format('MMMM YYYY')}
                                </TableHeaderCell>
                                <TableHeaderCell colSpan={2} textAlign='center'>
                                    {dayjs()
                                        .subtract(2, 'months')
                                        .format('MMMM YYYY')}
                                </TableHeaderCell>
                                <TableHeaderCell colSpan={2} textAlign='center'>
                                    {dayjs()
                                        .subtract(3, 'months')
                                        .format('MMMM YYYY')}
                                </TableHeaderCell>
                                <TableHeaderCell colSpan={2} textAlign='center'>
                                    {dayjs()
                                        .subtract(4, 'months')
                                        .format('MMMM YYYY')}
                                </TableHeaderCell>
                                <TableHeaderCell colSpan={2} textAlign='center'>
                                    {dayjs()
                                        .subtract(5, 'months')
                                        .format('MMMM YYYY')}
                                </TableHeaderCell>
                            </TableRow>
                            <TableRow>
                                <TableHeaderCell textAlign='center'>
                                    Datahub
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Platform
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Datahub
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Platform
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Datahub
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Platform
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Datahub
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Platform
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Datahub
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Platform
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Datahub
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Platform
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {statistics?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={16} />
                            )}

                            {statistics?.data?.map(statistic => (
                                <TableRow key={statistic.id}>
                                    <TableCell>
                                        {statistic.customer?.name}
                                    </TableCell>
                                    <TableCell>
                                        {statistic.entity.name}
                                    </TableCell>
                                    <TableCell>
                                        {dayjs().diff(
                                            dayjs(
                                                statistic.last_datahub_sync_at
                                            ),
                                            'day'
                                        ) >= 1 && (
                                            <ExclamationTriangleIcon className='size-4 text-red-600' />
                                        )}
                                        {localDateToMinutes(
                                            statistic.last_datahub_sync_at
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {dayjs().diff(
                                            dayjs(
                                                statistic.last_platform_sync_at
                                            ),
                                            'day'
                                        ) >= 1 && (
                                            <ExclamationTriangleIcon className='size-4 text-red-600' />
                                        )}
                                        {localDateToMinutes(
                                            statistic.last_platform_sync_at
                                        )}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_current_month ===
                                                statistic.platform_current_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_current_month >
                                                statistic.platform_current_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_current_month <
                                                statistic.platform_current_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.datahub_current_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_current_month ===
                                                statistic.platform_current_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_current_month >
                                                statistic.platform_current_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_current_month <
                                                statistic.platform_current_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.platform_current_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_one_month ===
                                                statistic.platform_one_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_one_month >
                                                statistic.platform_one_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_one_month <
                                                statistic.platform_one_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.datahub_one_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_one_month ===
                                                statistic.platform_one_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_one_month >
                                                statistic.platform_one_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_one_month <
                                                statistic.platform_one_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.platform_one_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_two_month ===
                                                statistic.platform_two_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_two_month >
                                                statistic.platform_two_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_two_month <
                                                statistic.platform_two_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.datahub_two_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_two_month ===
                                                statistic.platform_two_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_two_month >
                                                statistic.platform_two_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_two_month <
                                                statistic.platform_two_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.platform_two_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_three_month ===
                                                statistic.platform_three_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_three_month >
                                                statistic.platform_three_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_three_month <
                                                statistic.platform_three_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.datahub_three_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_three_month ===
                                                statistic.platform_three_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_three_month >
                                                statistic.platform_three_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_three_month <
                                                statistic.platform_three_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.platform_three_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_four_month ===
                                                statistic.platform_four_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_four_month >
                                                statistic.platform_four_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_four_month <
                                                statistic.platform_four_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.datahub_four_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_four_month ===
                                                statistic.platform_four_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_four_month >
                                                statistic.platform_four_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_four_month <
                                                statistic.platform_four_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.platform_four_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_five_month ===
                                                statistic.platform_five_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_five_month >
                                                statistic.platform_five_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_five_month <
                                                statistic.platform_five_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.datahub_five_month}
                                    </TableCell>
                                    <TableCell
                                        textAlign='center'
                                        className={cn(
                                            statistic.datahub_five_month ===
                                                statistic.platform_five_month &&
                                                'bg-green-50 text-green-700',
                                            statistic.datahub_five_month >
                                                statistic.platform_five_month &&
                                                'bg-red-50 text-red-700',
                                            statistic.datahub_five_month <
                                                statistic.platform_five_month &&
                                                'bg-yellow-50 text-yellow-700'
                                        )}
                                    >
                                        {statistic.platform_five_month}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={statistics?.meta}
                        only={['statistics']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default StatisticView;
